import React, { useEffect, useMemo, useRef, useState } from "react";
import {
	Alert,
	Box,
	Button,
	Chip,
	CircularProgress,
	Divider,
	Grid,
	Paper,
	Snackbar,
	Tooltip,
	Typography,
	useTheme,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { useSearchParams } from "react-router-dom";
import Header from "../../components/Header/Header.jsx";
import JsonSchemaBox from "../../components/Common/JsonSchemaBox.jsx";
import { tokens } from "../../theme.js";
import { useUserContext } from "../../contexts/userContext.js";
import {
	COVERAGE_LIST_TYPES,
	BLACK_WHITE_LISTS_TITLE,
	getCoverageTableConfig,
	isAddMode,
	isDeleteMode,
	validateCoverageRecord,
} from "./blackAndWhiteListsConfig.js";
import {
	createEmptyRecord,
	deleteCoverageRecord,
	fetchCoverageRecord,
	saveCoverageRecord,
} from "./blackAndWhiteListsApi.js";

const BlackAndWhiteLists = ({
	table: tableProp,
	id: idProp,
	mode: modeProp,
	onExit,
} = {}) => {
	const theme = useTheme();
	const colors = tokens(theme.palette.mode);
	const [searchParams] = useSearchParams();
	const userContext = useUserContext();
	const user = userContext?.getUser?.() || null;
	const parentHosted = typeof onExit === "function";

	const tableKey =
		(tableProp != null
			? String(tableProp)
			: searchParams.get("table") || ""
		)
			.trim()
			.toLowerCase() || "";
	const recordId =
		(idProp != null
			? String(idProp)
			: searchParams.get("id")?.trim() ||
			  searchParams.get("record_id")?.trim() ||
			  searchParams.get("recordId")?.trim() ||
			  ""
		).trim() || "";
	const mode =
		(modeProp != null
			? String(modeProp)
			: searchParams.get("mode") || "edit"
		)
			.trim()
			.toLowerCase() || "edit";
	const deleteMode = isDeleteMode(mode);
	const addMode = isAddMode(mode) && !recordId && !deleteMode;

	const tableConfig = useMemo(() => getCoverageTableConfig(tableKey), [tableKey]);

	const [isLoading, setIsLoading] = useState(!deleteMode);
	const [isSaving, setIsSaving] = useState(false);
	const [isDeleting, setIsDeleting] = useState(false);
	const [loadError, setLoadError] = useState("");
	const [record, setRecord] = useState(null);
	const [schema, setSchema] = useState({});
	const [formData, setFormData] = useState({});
	const [modifiedFields, setModifiedFields] = useState({});
	const [tabs, setTabs] = useState(["Details"]);
	const [isDeleted, setIsDeleted] = useState(false);
	const [snack, setSnack] = useState({
		open: false,
		status: "success",
		message: "",
	});

	const loadGenerationRef = useRef(0);

	const isReadOnlyUser =
		!!user && typeof user.hasRole === "function" && user.hasRole("ngom-user");

	const copyToClipboard = (value) => {
		if (!value) return;
		navigator.clipboard.writeText(String(value));
		setSnack({
			open: true,
			status: "success",
			message: "Copied to clipboard",
		});
	};

	useEffect(() => {
		const generation = ++loadGenerationRef.current;
		const isCurrent = () => loadGenerationRef.current === generation;

		setRecord(null);
		setSchema({});
		setFormData({});
		setModifiedFields({});
		setTabs(["Details"]);
		setLoadError("");
		setIsDeleted(false);

		const loadRecord = async () => {
			if (!tableConfig) {
				if (isCurrent()) {
					setLoadError("Invalid or missing table.");
					setIsLoading(false);
				}
				return;
			}

			if (deleteMode && !recordId) {
				if (isCurrent()) {
					setLoadError("Record ID is required.");
					setIsLoading(false);
				}
				return;
			}

			if (!addMode && !deleteMode && !recordId) {
				if (isCurrent()) {
					setLoadError("Record ID is required.");
					setIsLoading(false);
				}
				return;
			}

			if (deleteMode) {
				if (isCurrent()) {
					setIsLoading(false);
				}
				return;
			}

			if (isCurrent()) {
				setIsLoading(true);
			}

			try {
				if (addMode) {
					const empty = createEmptyRecord(tableKey);
					if (!isCurrent()) return;
					if (!empty) {
						setLoadError("Couldn't create empty record.");
						setIsLoading(false);
						return;
					}
					setRecord(empty.record);
					setSchema(empty.schema);
					setFormData(empty.formData);
					setModifiedFields({});
					setTabs(["Details"]);
				} else {
					const result = await fetchCoverageRecord(tableKey, recordId);
					if (!isCurrent()) return;
					setRecord(result.record);
					setSchema(result.schema);
					setFormData(result.formData);
					setModifiedFields({});
					setTabs(["Details"]);
				}
			} catch (error) {
				if (!isCurrent()) return;
				setLoadError(error?.message || "Couldn't load record.");
				setRecord(null);
				setSchema({});
				setFormData({});
			} finally {
				if (isCurrent()) {
					setIsLoading(false);
				}
			}
		};

		loadRecord();
	}, [addMode, deleteMode, recordId, tableConfig, tableKey]);

	const mergedRecord = useMemo(
		() => ({
			...record,
			...formData,
			...modifiedFields,
			id: record?.id || recordId || null,
		}),
		[record, formData, modifiedFields, recordId]
	);

	const validation = useMemo(
		() => validateCoverageRecord(mergedRecord, { addMode, tableConfig }),
		[mergedRecord, addMode, tableConfig]
	);

	const hasChanges = Object.keys(modifiedFields).length > 0;

	const canSave =
		validation.valid &&
		hasChanges &&
		!isReadOnlyUser &&
		!isSaving &&
		!isDeleting;

	const getSaveTooltip = () => {
		if (canSave) return "";
		if (!hasChanges) return "Change a field to save";
		return Object.values(validation.errors).join(" ");
	};

	const handleSave = async () => {
		if (!tableConfig || isReadOnlyUser || !canSave) return;

		setIsSaving(true);
		try {
			await saveCoverageRecord(tableKey, mergedRecord, {
				editMode: !addMode,
			});
			handleGoBack();
		} catch (error) {
			setSnack({
				open: true,
				status: "error",
				message: error?.message || "Couldn't save.",
			});
		} finally {
			setIsSaving(false);
		}
	};

	const handleDelete = async () => {
		if (!tableConfig || isReadOnlyUser || !recordId || isDeleted) return;

		setIsDeleting(true);

		try {
			await deleteCoverageRecord(tableKey, recordId);
			handleGoBack();
		} catch (error) {
			setSnack({
				open: true,
				status: "error",
				message: error?.message || "Couldn't delete.",
			});
		} finally {
			setIsDeleting(false);
		}
	};

	const handleGoBack = () => {
		if (parentHosted) {
			onExit();
			return;
		}
		if (window.history.length > 1) {
			window.history.back();
			return;
		}
		window.close();
	};

	const listTypeLabel =
		tableConfig?.listType === COVERAGE_LIST_TYPES.WHITELIST
			? "Whitelist"
			: "Blacklist";

	const listTypeColor =
		tableConfig?.listType === COVERAGE_LIST_TYPES.WHITELIST
			? colors.greenAccent[500]
			: colors.redAccent[500];

	const pageTitle = tableConfig
		? deleteMode
			? `Delete ${tableConfig.label}`
			: addMode
				? `Add ${tableConfig.label}`
				: `Edit ${tableConfig.label}`
		: BLACK_WHITE_LISTS_TITLE;

	const pageSubtitle = deleteMode
		? "This can't be undone"
		: addMode
			? tableConfig?.addSubtitle || "Required fields must be filled"
			: recordId
				? recordId
				: "";

	const formDisabled = isSaving || isDeleting;

	if (!tableConfig && !isLoading) {
		return (
			<Box m="20px">
				<Header title={BLACK_WHITE_LISTS_TITLE} subtitle="Invalid link" />
				<Alert severity="error" sx={{ maxWidth: 720 }}>
					Unknown table &quot;{tableKey || "—"}&quot;. Open this page from the
					black &amp; white lists dashboard.
				</Alert>
			</Box>
		);
	}

	return (
		<Box
			sx={{
				minHeight: "100%",
				backgroundColor: colors.primary[500],
			}}
		>
			<Box m="20px" sx={{ maxWidth: deleteMode ? 720 : 1200 }}>
				<Header title={pageTitle} subtitle={pageSubtitle} />

				{isLoading && (
					<Box
						sx={{
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
							minHeight: 320,
						}}
					>
						<CircularProgress color="secondary" />
					</Box>
				)}

				{!isLoading && loadError && (
					<Alert severity="error" sx={{ maxWidth: 720 }}>
						{loadError}
					</Alert>
				)}

				{deleteMode && !loadError && tableConfig && (
					<Paper
						elevation={3}
						sx={{
							backgroundColor: colors.primary[400],
							border: `1px solid ${colors.grey[700]}`,
							overflow: "hidden",
						}}
					>
						<Box sx={{ px: 3, py: 3 }}>
							<Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 3 }}>
								<Chip
									label={listTypeLabel}
									sx={{
										backgroundColor: listTypeColor,
										color: "#fff",
										fontWeight: 600,
									}}
								/>
								<Chip
									label={tableConfig.label}
									variant="outlined"
									sx={{
										borderColor: colors.grey[500],
										color: colors.grey[100],
									}}
								/>
								{recordId && (
									<Chip
										label={`ID: ${recordId}`}
										variant="outlined"
										sx={{
											maxWidth: "100%",
											color: colors.grey[100],
											borderColor: colors.grey[600],
										}}
										onClick={() => copyToClipboard(recordId)}
									/>
								)}
								{isDeleted && (
									<Chip label="Deleted" color="error" variant="filled" />
								)}
							</Box>

							{isDeleted ? (
								<Alert severity="success">Record deleted.</Alert>
							) : (
								<Alert
									severity="warning"
									icon={<WarningAmberIcon />}
									sx={{ mb: 1 }}
								>
									Remove this row from <strong>{tableConfig.label}</strong>?
								</Alert>
							)}

							{isReadOnlyUser && !isDeleted && (
								<Alert severity="error" sx={{ mt: 2 }}>
									You can't delete coverage list entries.
								</Alert>
							)}
						</Box>

						{!isDeleted && (
							<>
								<Divider sx={{ borderColor: colors.grey[700] }} />
								<Box
									sx={{
										px: 3,
										py: 2,
										display: "flex",
										justifyContent: "flex-end",
										gap: 1.5,
										backgroundColor: colors.primary[600],
									}}
								>
									<Button
										variant="contained"
										color="secondary"
										onClick={handleGoBack}
										disabled={isDeleting}
									>
										Cancel
									</Button>
									<Button
										variant="contained"
										color="error"
										startIcon={<DeleteOutlineIcon />}
										onClick={handleDelete}
										disabled={isReadOnlyUser || isDeleting}
									>
										{isDeleting ? "Deleting…" : "Delete"}
									</Button>
								</Box>
							</>
						)}
					</Paper>
				)}

				{!deleteMode && !isLoading && !loadError && record && (
					<Paper
						elevation={3}
						sx={{
							backgroundColor: colors.primary[400],
							border: `1px solid ${colors.grey[700]}`,
							overflow: "hidden",
						}}
					>
						<Box sx={{ px: 3, py: 2.5 }}>
							<Box
								sx={{
									display: "flex",
									flexWrap: "wrap",
									gap: 1,
									mb: 2,
								}}
							>
								<Chip
									label={listTypeLabel}
									sx={{
										backgroundColor: listTypeColor,
										color: "#fff",
										fontWeight: 600,
									}}
								/>
								<Chip
									label={tableConfig.label}
									variant="outlined"
									sx={{
										borderColor: colors.grey[500],
										color: colors.grey[100],
									}}
								/>
								{!addMode && record.id && (
									<Chip
										label={`ID: ${record.id}`}
										sx={{
											maxWidth: "100%",
											color: colors.grey[100],
											borderColor: colors.grey[600],
										}}
										variant="outlined"
										onClick={() => copyToClipboard(record.id)}
									/>
								)}
								{addMode && (
									<Chip
										label="New record"
										color="secondary"
										variant="outlined"
									/>
								)}
							</Box>

							{hasChanges && (
								<Alert severity="info" sx={{ mb: 2 }}>
									Unsaved changes
								</Alert>
							)}

							<Divider sx={{ mb: 2, borderColor: colors.grey[700] }} />

							<JsonSchemaBox
								key={`${tableKey}-${addMode ? "add" : "edit"}-${record?.id || recordId || "new"}`}
								schema={schema}
								formData={formData}
								onOnlyModifiedDataChange={setModifiedFields}
								tabs={tabs}
								spacing={2}
								padding={1}
							/>
						</Box>

						<Divider sx={{ borderColor: colors.grey[700] }} />

						<Box
							sx={{
								px: 3,
								py: 2,
								display: "flex",
								justifyContent: isReadOnlyUser ? "space-between" : "flex-end",
								alignItems: "center",
								flexWrap: "wrap",
								gap: 2,
								backgroundColor: colors.primary[600],
							}}
						>
							{isReadOnlyUser && (
								<Typography variant="body2" color={colors.grey[300]}>
									Read-only access
								</Typography>
							)}

							<Grid container spacing={1.5} justifyContent="flex-end" sx={{ width: "auto" }}>
								<Grid item>
									<Button
										variant="contained"
										color="secondary"
										onClick={handleGoBack}
										disabled={isSaving}
									>
										Cancel
									</Button>
								</Grid>
								<Grid item>
									<Tooltip title={getSaveTooltip()}>
										<span>
											<Button
												variant="contained"
												color="secondary"
												startIcon={<SaveOutlinedIcon />}
												onClick={handleSave}
												disabled={
													formDisabled || isReadOnlyUser || !canSave
												}
											>
												{isSaving ? "Saving…" : "Save"}
											</Button>
										</span>
									</Tooltip>
								</Grid>
							</Grid>
						</Box>
					</Paper>
				)}
			</Box>

			<Snackbar
				open={snack.open}
				autoHideDuration={5000}
				onClose={() => setSnack((prev) => ({ ...prev, open: false }))}
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
			>
				<Alert
					onClose={() => setSnack((prev) => ({ ...prev, open: false }))}
					severity={snack.status}
					variant="filled"
					sx={{ width: "100%" }}
				>
					{snack.message}
				</Alert>
			</Snackbar>
		</Box>
	);
};

export default BlackAndWhiteLists;
